import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Artwork from '../components/Artwork.jsx'
import Avatar from '../components/Avatar.jsx'
import ErrorState, { LoadingState } from '../components/ErrorState.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import FollowButton from '../components/FollowButton.jsx'
import NewListForm from '../components/NewListForm.jsx'
import ReviewCard from '../components/ReviewCard.jsx'
import ScorePill from '../components/ScorePill.jsx'
import { ApiError, api } from '../lib/api.js'
import { plural } from '../lib/format.js'
import { display } from '../lib/score.js'

/**
 * `/profile/:handle` — anybody's profile, with tabs (SOC-001, PLAN §7).
 *
 * Four states, and they are genuinely different pages:
 *
 * * **yours** — every tab, including drafts, and an edit link;
 * * **theirs** — public ratings, published reviews, public lists;
 * * **private, not approved** — the name, the handle and a follow button, and
 *   nothing else. Enough to ask, and nothing to read;
 * * **blocked** — no counts, no bio, no tabs. Which direction the block runs
 *   changes only whether an unblock button appears, because a button that
 *   cannot work is worse than no button;
 * * **gone** — deactivated inside its grace period, or purged. 1A-5 added
 *   both, and they are separate screens because "this account is on its way
 *   out" and "this account no longer exists" are different answers, and
 *   neither is "this account is private".
 */
const TABS = [
  { key: 'ratings', label: 'Ratings' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'lists', label: 'Lists' },
]

function RatedRow({ item }) {
  return (
    <li className="rated-row">
      <Link
        to={`/${item.kind === 'song' ? 'song' : 'album'}/${item.public_id}`}
        className="rated-media"
      >
        <Artwork
          art={item.artwork_url ? { url: item.artwork_url } : null}
          title={item.title}
          artist={item.credit_name}
          size={44}
        />
        <span className="row-main">
          <span className="rated-title">{item.title}</span>
          <span className="footnote">
            {item.credit_name}
            {item.year ? ` · ${item.year}` : ''}
          </span>
          {item.first_impression_score != null && item.settled_score != null ? (
            /* The quiet fact (1B-2 Q2): where it started and where it landed.
             * Only ever present on your own rows — history is its owner's. */
            <span className="footnote">
              First impression {display(item.first_impression_score)} → settled{' '}
              {display(item.settled_score)}
            </span>
          ) : null}
        </span>
      </Link>
      <ScorePill score={item.score} mine label={`Score ${item.score}`} />
    </li>
  )
}

function Blocked({ profile, onChange }) {
  const theyBlocked = profile.blocked_me
  // This screen's own sentence, beside its own button, the way FollowButton
  // keeps one — it is a component with its own request to make.
  const [error, setError] = useState('')

  async function unblock() {
    setError('')
    try {
      await api.unblock(profile.username)
      onChange()
    } catch (err) {
      // `onChange()` on success only: a refused unblock leaves this screen
      // exactly as it is, because the block is exactly as it was.
      setError(err instanceof ApiError ? err.message : 'Could not unblock this account just now.')
    }
  }

  return (
    <div className="card card-pad fade-up">
      <div className="empty-state">
        <h2>@{profile.username}</h2>
        <p>
          {theyBlocked
            ? 'You cannot see this account, and it cannot see you.'
            : 'You blocked this account. Neither of you can see the other.'}
        </p>
        {theyBlocked ? null : (
          <>
            <button type="button" className="btn btn-tonal" onClick={unblock}>
              Unblock
            </button>
            <InlineAlert>{error}</InlineAlert>
          </>
        )}
      </div>
    </div>
  )
}

function Gone({ profile }) {
  const purged = profile.account_status === 'deleted'
  return (
    <div className="card card-pad fade-up">
      <div className="empty-state">
        <h2>{purged ? 'This account was deleted' : 'This account is on its way out'}</h2>
        <p>
          {purged
            ? 'Anything of theirs still on Sonologue is here because they chose to leave it, without their name.'
            : 'They have asked for it to be deleted. Nothing of theirs is visible while that is pending.'}
        </p>
      </div>
    </div>
  )
}

function Private({ profile, onChange }) {
  return (
    <div className="card card-pad fade-up">
      <div className="empty-state">
        <h2>{profile.display_name}</h2>
        <p>
          @{profile.username} keeps a private account.{' '}
          {profile.follow_state === 'pending'
            ? 'Your request is waiting for them.'
            : 'Ask to follow and they decide.'}
        </p>
        <FollowButton handle={profile.username} state={profile.follow_state} onChange={onChange} />
      </div>
    </div>
  )
}

/**
 * *New list*, on your own Lists tab only (Q19). The form opens in the panel and
 * a made list opens on its own page, where the empty state already says how
 * to fill it. Cancel hands focus back to the button that opened the form.
 */
function NewListRow() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const button = useRef(null)
  const opened = useRef(false)

  useEffect(() => {
    if (open) opened.current = true
    else if (opened.current) button.current?.focus()
  }, [open])

  return (
    <div className="card-pad">
      {open ? (
        <NewListForm
          submitLabel="Create list"
          onSubmit={async (body) => {
            const list = await api.createList(body)
            navigate(`/lists/${list.public_id}`)
          }}
          onCancel={() => setOpen(false)}
        />
      ) : (
        <button
          ref={button}
          type="button"
          className="btn btn-tonal btn-sm"
          onClick={() => setOpen(true)}
        >
          New list
        </button>
      )}
    </div>
  )
}

export default function Profile() {
  const { handle } = useParams()
  const [profile, setProfile] = useState(null)
  const [tab, setTab] = useState('ratings')
  const tabRefs = useRef([])
  const [data, setData] = useState({})
  const [error, setError] = useState('')
  // A tab that fails is not a profile that failed, so it keeps its own state
  // and its own retry. `tabAttempt` is what that retry bumps: the tab effect
  // already knows how to fetch the current tab, and re-running it is the only
  // thing the retry has to mean.
  const [tabError, setTabError] = useState(false)
  const [tabAttempt, setTabAttempt] = useState(0)
  // Block's own sentence, under the button, beside FollowButton's.
  const [blockError, setBlockError] = useState('')

  function onTabKey(event) {
    const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' }
    const move = keys[event.key]
    if (move === undefined) return
    event.preventDefault()
    const at = TABS.findIndex((entry) => entry.key === tab)
    const next =
      move === 'first'
        ? 0
        : move === 'last'
          ? TABS.length - 1
          : (at + move + TABS.length) % TABS.length
    setTab(TABS[next].key)
    tabRefs.current[next]?.focus()
  }

  async function block() {
    setBlockError('')
    try {
      await api.block(profile.username)
      loadProfile()
    } catch (err) {
      // No reload: nothing changed, so asking again would only cost a request.
      setBlockError(err instanceof ApiError ? err.message : 'Could not block this account just now.')
    }
  }

  const loadProfile = useCallback(
    async (signal) => {
      try {
        setProfile(await api.profile(handle, { signal }))
      } catch (err) {
        if (err.name === 'AbortError') return
        setError(err instanceof ApiError ? err.message : 'That profile did not load.')
      }
    },
    [handle],
  )

  useEffect(() => {
    document.title = `@${handle} — Sonologue`
    const controller = new AbortController()
    setProfile(null)
    setError('')
    setData({})
    loadProfile(controller.signal)
    return () => controller.abort()
  }, [handle, loadProfile])

  const visible = profile?.visible
  useEffect(() => {
    if (!visible) return undefined
    const controller = new AbortController()
    // Clears on a tab or handle change too, because both re-run this effect.
    setTabError(false)
    const fetchers = {
      ratings: () => api.profileRatings(handle, { signal: controller.signal }),
      reviews: () => api.profileReviews(handle, { signal: controller.signal }),
      lists: () => api.profileLists(handle, { signal: controller.signal }),
    }
    fetchers[tab]()
      .then((body) => setData((current) => ({ ...current, [tab]: body })))
      .catch((err) => {
        if (err.name !== 'AbortError') setTabError(true)
      })
    return () => controller.abort()
  }, [handle, tab, visible, tabAttempt])

  if (error) {
    return (
      <ErrorState
        title="That profile did not load"
        message={error}
        onRetry={() => loadProfile()}
        level={1}
      />
    )
  }
  if (!profile) return <LoadingState label="Loading profile" />

  if (profile.i_blocked || profile.blocked_me) {
    return <Blocked profile={profile} onChange={() => loadProfile()} />
  }
  // Order matters: an account that is gone is gone whether or not it was
  // private, and "private" is the wrong thing to tell somebody about it.
  if (profile.account_status && profile.account_status !== 'active') {
    return <Gone profile={profile} />
  }
  if (!profile.visible) return <Private profile={profile} onChange={() => loadProfile()} />

  const counts = profile.rating_counts || {}
  const payload = data[tab]

  return (
    <>
      <div className="card card-pad fade-up profile-head">
        <Avatar
          name={profile.display_name || profile.username}
          avatarUrl={profile.avatar_url}
          size={64}
        />
        <div className="row-main">
          <h1 className="title-2 mb-0">{profile.display_name}</h1>
          <p className="footnote mt-0.5 mb-0">
            @{profile.username}
            {profile.is_private ? ' · private' : ''}
            {profile.follows_me && !profile.is_me ? ' · follows you' : ''}
          </p>
          {profile.bio ? <p className="mt-2.5 mb-0">{profile.bio}</p> : null}
          <p className="footnote mt-2 mb-0">
            <strong>{profile.followers}</strong> {plural(profile.followers, 'follower')} ·{' '}
            <strong>{profile.following}</strong> following ·{' '}
            <strong>{counts.albums || 0}</strong> {plural(counts.albums || 0, 'album')} ·{' '}
            <strong>{profile.review_count}</strong> {plural(profile.review_count, 'review')}
          </p>
        </div>
        <div className="profile-actions">
          {profile.is_me ? (
            <Link className="btn btn-quiet btn-sm" to="/settings">
              Edit
            </Link>
          ) : (
            <>
              <FollowButton
                handle={profile.username}
                state={profile.follow_state}
                blockedMe={profile.blocked_me}
                onChange={() => loadProfile()}
              />
              {profile.visible ? (
                <Link className="btn btn-quiet btn-sm" to={`/compare/${profile.username}`}>
                  Compare taste
                </Link>
              ) : null}
              <button type="button" className="btn btn-quiet btn-sm" onClick={block}>
                Block
              </button>
              <InlineAlert>{blockError}</InlineAlert>
            </>
          )}
        </div>
      </div>

      {/*
        A tablist is a single stop in the Tab order, and the arrow keys move
        between the tabs inside it (WAI-ARIA authoring practices). Without the
        roving `tabIndex` a keyboard user Tabs through every tab to reach the
        panel; without the arrow keys they cannot change tab at all once they
        are in it.
      */}
      <div className="tabs" role="tablist" aria-label="Profile sections" onKeyDown={onTabKey}>
        {TABS.map((entry, index) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            id={`tab-${entry.key}`}
            aria-selected={tab === entry.key}
            aria-controls="profile-panel"
            tabIndex={tab === entry.key ? 0 : -1}
            ref={(node) => {
              tabRefs.current[index] = node
            }}
            className={`tab ${tab === entry.key ? 'active' : ''}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="card" role="tabpanel" id="profile-panel" aria-labelledby={`tab-${tab}`}>
        {/*
          Before the loading branch: a failed fetch leaves `data[tab]`
          undefined, so a tab that never arrives would otherwise say
          "Loading…" for as long as the page is open. The body is
          `ErrorState`'s default sentence rather than the component itself —
          the panel is already a card, and a card inside a card draws two
          borders.
        */}
        {tabError ? (
          <div className="empty-state">
            <h2>That tab did not load</h2>
            <p role="alert">Something went wrong on our side. This is usually temporary.</p>
            <button
              type="button"
              className="btn btn-tonal"
              onClick={() => setTabAttempt((attempt) => attempt + 1)}
            >
              Try again
            </button>
          </div>
        ) : payload === undefined ? (
          <div className="card-pad">
            <p className="footnote mb-0">Loading…</p>
          </div>
        ) : tab === 'ratings' ? (
          payload.albums?.length || payload.songs?.length ? (
            <ul className="rated-list">
              {[...(payload.albums || []), ...(payload.songs || [])].map((item) => (
                <RatedRow key={`${item.kind}-${item.public_id}`} item={item} />
              ))}
            </ul>
          ) : (
            <div className="empty-state">
              <h2>No ratings yet</h2>
              <p>{profile.is_me ? 'Rate something and it lands here.' : 'Nothing public yet.'}</p>
            </div>
          )
        ) : tab === 'reviews' ? (
          payload.length ? (
            <div className="review-stack card-pad">
              {payload.map((review) => (
                <ReviewCard key={review.public_id} review={review} showMedia />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <h2>No reviews yet</h2>
              <p>{profile.is_me ? 'Drafts show here too.' : 'Nothing published yet.'}</p>
            </div>
          )
        ) : (
          <>
            {profile.is_me ? <NewListRow /> : null}
            {payload.length ? (
              <ul className="list-index">
                {payload.map((row) => (
                  <li key={row.public_id}>
                    <Link to={`/lists/${row.public_id}`} className="list-index-row">
                      <span className="row-main">
                        <span className="rated-title">{row.title}</span>
                        <span className="footnote">
                          {row.item_count} {row.item_count === 1 ? 'entry' : 'entries'}
                          {row.visibility === 'private' ? ' · private' : ''}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="empty-state">
                <h2>No lists yet</h2>
                <p>{profile.is_me ? 'Start one from any album page.' : 'Nothing public yet.'}</p>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
